import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { FocusContext, useFocusable, setFocus } from '@noriginmedia/norigin-spatial-navigation';
import { FocusableButton } from '@/components/common/FocusableButton';
import { FocusableSearchButton } from '@/components/layout/FocusableSearchButton';
import { ProfileMenu } from '@/components/layout/ProfileMenu';
import { LogoIcon } from '@/components/svg';
import { useNavigation } from './useNavigation';
import { useUserAuth } from './useUserAuth';
import { getMaskImageClassName, getMaskImageInlineStyle } from './utils';

import styles from '../Header.module.scss';
import { filterEmoji } from '@/utils/filterEmoji';
import { generatePaySessionId } from '@/utils/paySessionId';

export const Header: React.FC<{ skipAutoFocus?: boolean }> = ({ skipAutoFocus = false }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [paySessionId, setPaySessionId] = useState('');

  // 为 Header 创建焦点上下文（不需要渲染 ref，只是提供上下文）
  const { focusKey } = useFocusable({
    focusable: false, // Header 本身不可获焦，只是提供上下文
    trackChildren: true,
  });

  // 导航相关逻辑
  const {
    navItems,
    navContainerRef,
    navScrollRef,
    handlers,
    state,
    handleNavScroll,
    lastFocusedKeyRef,
    setIsNavFocused,
  } = useNavigation(skipAutoFocus);

  const {
    handleNavFocus,
    handleNavClick,
    handleNavArrowPress,
    handleMoreFocus,
    handleMoreClick,
    handleSearchFocus,
    handleSearchClick,
  } = handlers;

  const { focusedNavItem, focusedNavKey, highlightState, isNavFocused, navScrollState } = state;

  // 用户认证相关逻辑
  const { isLoggedIn, userAvatar, userNick, vipInfo, handleProfileMenuSelect, handleJoinVip } = useUserAuth(
    location.pathname
  );

  // 辅助函数：延迟设置焦点
  const setFocusDelayed = useCallback((focusKey: string) => {
    setTimeout(() => setFocus(focusKey), 0);
  }, []);

  // 计算 mask 样式
  const { showLeftGradient, showRightGradient } = navScrollState;
  const maskClassName = useMemo(
    () => getMaskImageClassName(showLeftGradient, showRightGradient, styles),
    [showLeftGradient, showRightGradient]
  );

  const maskInlineStyle = useMemo(
    () => getMaskImageInlineStyle(showLeftGradient, showRightGradient),
    [showLeftGradient, showRightGradient]
  );

  useEffect(() => {
    setPaySessionId(generatePaySessionId());
  }, []);

  return (
    <FocusContext.Provider value={focusKey}>
      <header className={styles.header}>
        {/* 左侧：个人中心和导航 */}
        <div className={styles.leftSection}>
          {/* 个人中心组件 */}
          <ProfileMenu
            focusKey="header-profile"
            dt-eid="navigate_bar_user"
            dt-params="mod_id=navbar&button_name=profile"
            isLoggedIn={isLoggedIn}
            userName={userNick}
            userAvatar={userAvatar}
            vipInfo={vipInfo}
            onMenuSelect={handleProfileMenuSelect}
            onFocusChange={() => {
              // 焦点离开导航区域，高亮层变为半透明
              setIsNavFocused(false);
            }}
          />

          {/* Join VIP 按钮 - 在登录入口右边 */}
          <FocusableButton
            dt-eid="navigate_bar_user"
            dt-params={`mod_id=navbar&button_name=vip&pay_session_id=${paySessionId}`}
            focusKey="header-join-vip"
            className={styles.joinVipButton}
            focusedClassName={styles.joinVipButtonFocused}
            onClick={handleJoinVip}
            onFocusChange={() => {
              lastFocusedKeyRef.current = 'header-join-vip';
              // 焦点离开导航区域，高亮层变为半透明
              setIsNavFocused(false);
            }}
            onArrowPress={(direction) => {
              // 向上切焦时阻止移动（返回 false），保持焦点不动
              if (direction === 'up') {
                return false;
              }
              // 向左切焦时，导航到个人中心入口
              if (direction === 'left') {
                setFocusDelayed('header-profile-entry');
                return false; // 阻止默认导航，我们手动处理
              }
              // 向右切焦时，导航到搜索按钮
              if (direction === 'right') {
                setFocusDelayed('nav-search');
                return false; // 阻止默认导航，我们手动处理
              }
              // 向下切焦允许，切到页面内容元素
              return true;
            }}
          >
            VIP
          </FocusableButton>
        </div>

        {/* 导航区域 - 限制宽度以保留右侧 336px 空间 */}
        <nav className={styles.navWrapper}>
          {/* 搜索按钮 - 固定不滚动 */}
          <FocusableSearchButton
            dt-eid="navigate_bar_user"
            dt-params="mod_id=navbar&button_name=search"
            focusKey="nav-search"
            onClick={handleSearchClick}
            onFocusChange={handleSearchFocus}
            onArrowPress={(direction) => {
              // 向上切焦时阻止移动（返回 false），保持焦点不动
              if (direction === 'up') {
                return false;
              }
              // 向左导航到 VIP 按钮
              if (direction === 'left') {
                // 导航到 VIP 按钮
                setFocusDelayed('header-join-vip');
                return false; // 阻止默认导航，我们手动处理
              }
              // 向右：如果有频道 tabs，允许默认导航；如果没有，导航到"更多"按钮
              if (direction === 'right') {
                if (navItems.length === 0) {
                  // 没有频道 tabs，直接导航到"更多"按钮
                  setFocusDelayed('nav-more');
                  return false;
                }
                // 有频道 tabs，允许默认导航
                return true;
              }
              return true;
            }}
            className={styles.searchButton}
            iconClassName={styles.searchButtonIcon}
            {...({ 'data-focus-key': 'nav-search' } as any)}
          />

          {/* 导航容器（可滚动） */}
          <div
            ref={navScrollRef}
            className={`${styles.nav} ${maskClassName}`}
            style={maskInlineStyle}
            onScroll={handleNavScroll}
          >
            <div ref={navContainerRef} className={styles.navContent}>
              {/* 高亮层 - 独立的绝对定位元素，跟随焦点移动 */}
              {highlightState.visible && (
                <div
                  className={`${styles.navHighlight} ${!isNavFocused ? styles.inactive : ''}`}
                  style={{
                    left: highlightState.left,
                    width: highlightState.width,
                    zIndex: highlightState.zIndex || 1,
                  }}
                />
              )}

              {/* 导航标签 */}
              {navItems.map((item) => {
                const channelKey = `nav-channel-${item.channelId}-${item.channelType}`;
                const isActive = focusedNavItem?.channelId === item.channelId;
                const isFocused = focusedNavKey === channelKey;
                const isInactive = isFocused && !isNavFocused;

                const navButtonClass = [
                  styles.navButton,
                  isActive && styles.active,
                  isFocused && styles.navButtonFocused,
                  isInactive && styles.inactive,
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <FocusableButton
                    key={item.path}
                    focusKey={channelKey}
                    onClick={() => handleNavClick(item.path)}
                    onArrowPress={(direction) => handleNavArrowPress(direction, item.path)}
                    onFocusChange={() => handleNavFocus(item.path, channelKey, item)}
                    onMouseEnter={() => {
                      // 鼠标悬停时触发与左右切焦相同的逻辑
                      handleNavFocus(item.path, channelKey, item);
                      // 手动设置焦点到当前项
                      setFocusDelayed(channelKey);
                    }}
                    onBackPress={() => {
                      // 空实现
                    }}
                    className={navButtonClass}
                    focusedClassName={styles.navButtonFocusedEmpty}
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      transform: 'scale(1)',
                    }}
                    {...({ 'data-focus-key': channelKey } as any)}
                  >
                    {filterEmoji(item.label)}
                  </FocusableButton>
                );
              })}

              {/* 更多按钮 - 固定在最后 */}
              <FocusableButton
                focusKey="nav-more"
                onClick={handleMoreClick}
                onArrowPress={(direction) => {
                  // 向左：如果没有频道 tabs，导航到搜索按钮
                  if (direction === 'left' && navItems.length === 0) {
                    setFocusDelayed('nav-search');
                    return false;
                  }
                  return handleNavArrowPress(direction, '/more/0');
                }}
                onFocusChange={location.pathname.startsWith('/more') ? undefined : handleMoreFocus}
                onMouseEnter={() => {
                  // 鼠标悬停时触发与左右切焦相同的逻辑
                  if (!location.pathname.startsWith('/more')) {
                    handleMoreFocus();
                  }
                  // 手动设置焦点到更多按钮
                  setFocusDelayed('nav-more');
                }}
                onBackPress={() => {
                  // 空实现
                }}
                className={[
                  styles.navButton,
                  location.pathname.startsWith('/more') && styles.active,
                  focusedNavKey === 'nav-more' && styles.navButtonFocused,
                  focusedNavKey === 'nav-more' && !isNavFocused && styles.inactive,
                ]
                  .filter(Boolean)
                  .join(' ')}
                focusedClassName={styles.navButtonFocusedEmpty}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  transform: 'scale(1)',
                }}
                {...({ 'data-focus-key': 'nav-more' } as any)}
              >
                {t('nav.more')}
              </FocusableButton>
            </div>
          </div>
        </nav>

        {/* 右侧：Logo - 不可聚焦 */}
        <div className={styles.rightSection}>
          <LogoIcon className={styles.logo} />
        </div>
      </header>
    </FocusContext.Provider>
  );
};
